import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarDays, ChevronLeft, ChevronRight, History, Loader2, RefreshCw, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import LeadStatusBadge from '@/components/ui-custom/LeadStatusBadge';
import { fetchLeadDuplicates, type DuplicateLeadEntry } from '@/api/leadDuplicates';
import type { LeadStageType } from '@/context/LeadContext';
import { serviceLabel } from '@/lib/serviceCatalog';
import { cn } from '@/lib/utils';

const dateFormat = new Intl.DateTimeFormat('el-GR', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Athens',
});
const knownStages = new Set(['Νέο', 'σεΕκκρεμότητα', 'έκλεισεΗΔουλειά', 'ήθελεΠληροφορίες']);

function HistoryEntry({ entry, current = false, onNavigate }: {
  entry: DuplicateLeadEntry; current?: boolean; onNavigate: () => void;
}) {
  const date = entry.created_at ? new Date(entry.created_at) : null;
  return (
    <article className={cn('min-w-0 rounded-xl border p-4', current ? 'border-blue-200 bg-blue-50/70' : 'border-slate-200 bg-white')}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className={cn('font-semibold', current ? 'text-blue-700' : 'text-slate-500')}>
          {current ? 'Η καταχώρηση που άνοιξες' : 'Άλλη εμφάνιση'}
        </span>
        <span className="flex items-center gap-1.5 text-slate-600">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
          {date && !Number.isNaN(date.getTime())
            ? <time dateTime={entry.created_at!}>{dateFormat.format(date)}</time>
            : 'Χωρίς ημερομηνία'}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="min-w-0 break-words font-semibold text-slate-900 [overflow-wrap:anywhere]">{entry.name || 'Χωρίς όνομα'}</h3>
        {knownStages.has(entry.stage)
          ? <LeadStatusBadge status={entry.stage as LeadStageType} className="shrink-0" />
          : <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">{entry.stage || 'Χωρίς στάδιο'}</span>}
      </div>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        <div className="min-w-0"><dt className="text-xs text-slate-500">Υπηρεσία</dt><dd className="mt-1 break-words font-medium text-slate-800">{serviceLabel(entry.service)}</dd></div>
        <div className="min-w-0"><dt className="text-xs text-slate-500">Τωρινός συνεργάτης</dt><dd className={cn('mt-1 flex items-start gap-1.5 break-words font-medium', entry.partner_id ? 'text-slate-800' : 'text-amber-700')}>
          <UserRound className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 [overflow-wrap:anywhere]">{entry.partner_id ? entry.partner_name || 'Μη διαθέσιμο όνομα συνεργάτη' : 'Χωρίς ανάθεση'}</span>
        </dd></div>
      </dl>
      <Button asChild variant="link" className="mt-2 min-h-11 px-0 text-blue-700">
        <Link to={`/admin/lead/${encodeURIComponent(entry.id)}`} onClick={onNavigate} aria-label={`Άνοιγμα επαφής ${entry.name}, ${date && !Number.isNaN(date.getTime()) ? dateFormat.format(date) : 'χωρίς ημερομηνία'}`}>
          Άνοιγμα επαφής <ArrowUpRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </Link>
      </Button>
    </article>
  );
}

// Mounted only while the dialog is open: no per-card requests on list loads.
function HistoryContent({ leadId, onNavigate }: { leadId: string; onNavigate: () => void }) {
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['lead-duplicates', leadId, page],
    queryFn: ({ signal }) => fetchLeadDuplicates(leadId, page, signal),
    staleTime: 0, gcTime: 0, retry: false,
  });
  if (query.isPending) return <div role="status" className="flex items-center justify-center gap-2 p-10 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Φόρτωση ιστορικού…</div>;
  if (query.isError) return <div role="alert" className="space-y-3 rounded-xl bg-rose-50 p-5 text-sm text-rose-800"><p>{query.error.message}</p><Button variant="outline" onClick={() => void query.refetch()}>Δοκιμή ξανά</Button></div>;
  const { current, data, pagination } = query.data;
  return (
    <>
      <div className="min-h-0 space-y-4 overflow-y-auto overscroll-contain px-5 pb-2 sm:px-6">
        <HistoryEntry entry={current} current onNavigate={onNavigate} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Άλλες εμφανίσεις <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{pagination.total}</span></h3>
          <Button variant="ghost" size="sm" className="min-h-11 text-slate-500" disabled={query.isFetching} onClick={() => void query.refetch()}>
            <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', query.isFetching && 'animate-spin')} /> Ανανέωση
          </Button>
        </div>
        {data.length ? <div className="space-y-3">{data.map(entry => <HistoryEntry key={entry.id} entry={entry} onNavigate={onNavigate} />)}</div>
          : <p className="rounded-xl border border-dashed p-5 text-sm text-slate-500">Δεν υπάρχουν πλέον άλλες καταχωρήσεις με το ίδιο τηλέφωνο.</p>}
        <p className="text-xs leading-relaxed text-slate-500">Νεότερες πρώτα. Ώρα Ελλάδας. Οι αναθέσεις και τα στάδια δείχνουν τη σημερινή εικόνα κάθε καταχώρησης, όχι ιστορικό αλλαγών.</p>
      </div>
      {pagination.total_pages > 1 && <nav aria-label="Σελίδες ιστορικού επαφής" className="flex shrink-0 items-center justify-between gap-2 border-t px-5 py-3">
        <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Προηγούμενη σελίδα ιστορικού" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm text-slate-600">{pagination.page} / {pagination.total_pages}</span>
        <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Επόμενη σελίδα ιστορικού" disabled={pagination.page >= pagination.total_pages} onClick={() => setPage(pagination.page + 1)}><ChevronRight className="h-4 w-4" /></Button>
      </nav>}
    </>
  );
}

export default function LeadDuplicateHistory({ leadId, name }: { leadId: string; name: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" onClick={event => event.stopPropagation()} aria-label={`Διπλότυπο: ιστορικό επαφής ${name}`}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-800 transition-colors hover:border-orange-300 hover:bg-orange-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 sm:min-h-7">
          <History className="h-3.5 w-3.5" aria-hidden="true" /> Διπλότυπο <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl p-0 pb-4 [&>button]:right-2 [&>button]:top-2 [&>button]:flex [&>button]:h-11 [&>button]:w-11 [&>button]:items-center [&>button]:justify-center" onClick={event => event.stopPropagation()}>
        <DialogHeader className="shrink-0 border-b px-5 py-5 pr-14 text-left sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-slate-900"><History className="h-5 w-5 shrink-0 text-orange-600" />Ιστορικό ίδιας επαφής</DialogTitle>
          <DialogDescription className="pt-1 text-xs leading-relaxed sm:text-sm">Καταχωρήσεις με το ίδιο τηλέφωνο σε όλο το CRM. Μπορεί να αφορούν διαφορετικό αίτημα ή υπηρεσία.</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col pt-4">{open && <HistoryContent leadId={leadId} onNavigate={() => setOpen(false)} />}</div>
      </DialogContent>
    </Dialog>
  );
}
